/*------------------------------------------------------------------------------
   About      : Currently this manager deals with data being stored on
                localStorage. Eventually this should be responsible for all
                session related functions.

   Created on : Thu Nov 18 2021
   Author     : Siddharth Garg

   Copyright (c) 2021 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/

import { EnhancedStore }                from "@reduxjs/toolkit";
import { CMSPilotApis }                 from "apis/cmsPilotAPI";
import { RunContextApp } 						    from "framework/RCApp"
import { setApplianceProfile }          from "store/dataReducers/applianceProfileSlice";
import { setHomeProfile }               from "store/dataReducers/homeProfileSlice";
import { selectCMSPilot, 
         setSelectedPilotRecoScore,
         upsertCMSPilot
       }                                from "store/dataReducers/cmsPilotSlice"
import { resetRecoState }               from "store/dataReducers/recommendationsSlice"
import { UserAuthInfo, 
         UserInfo
       }                                from "types/apis/sessionInterfaces"
import { GetCMSPilotData }              from "types/apis/cmsPilotInterfaces";
import { APIData }                      from "types/cms/frontendTypes";
import { SessionApis }                  from "apis/sessionAPI";
import { resetRecoCommentsCount }       from "store/dataReducers/recoCommentsSlice";

export class SessionManager {

  private isLoggedIn        : boolean = false;
  private cmsAccessToken    : string = ''
  private user             !: UserInfo
  private store            !: EnhancedStore
  
	constructor(private rc : RunContextApp) {}

  setStore(store : EnhancedStore) {
    this.store = store
  }

	getUserLocale() {
		return ''
	}

  async init(userInfo ?: UserAuthInfo) : Promise<string | undefined> {

    if (userInfo) {
      this.cmsAccessToken = userInfo.jwt
      this.user = userInfo.user

      localStorage.setItem("accessToken", this.cmsAccessToken)
      localStorage.setItem("userInfo", JSON.stringify(this.user))

      return this.buildSession()

    } else { // Try to load from localStorage
      const cmsAccessToken = localStorage.getItem("accessToken")
      const userInfo = localStorage.getItem("userInfo")

      if (!cmsAccessToken || !userInfo) {
        return "Invalid session"
      }

      this.cmsAccessToken = cmsAccessToken
      this.user = JSON.parse(userInfo)

      return this.buildSession()
    }
  }

  private async buildSession() : Promise<string | undefined> {

    this.rc.cmsApiClient.init(this.cmsAccessToken)
      
    const userRoleInfoError = await this.requestUserRoleInfo()
    if (userRoleInfoError) {
      return userRoleInfoError
    }

    this.isLoggedIn = true
  }

  private async requestUserRoleInfo() : Promise<string | undefined> {

    const userRoleInfoResp = await SessionApis.getUserRoleInfo(this.rc)
    if (userRoleInfoResp.error) {
      return userRoleInfoResp.error.message
    } 

    const userRoleInfo = userRoleInfoResp.data!
    this.rc.permissionsManager.setUserRoleInfo(userRoleInfo)
    return
  }

  getUserInfo() {  
    return this.user
  }

  getCMSAccessToken() {
    return this.cmsAccessToken
  }

  isUserLoggedIn() {
    return this.isLoggedIn
  }

  logoutUser() {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userInfo");
    window.location.reload();
  }

  async selectCMSPilot(utility : APIData<"api::utility.utility">) {
   
    const pilotDataResp = await CMSPilotApis.getPilotData(this.rc, utility.documentId)
    if (pilotDataResp.error) {
      return pilotDataResp.error
    }
    this.populateCMSPilotData(pilotDataResp.data!)
  }

  async selectCMSPilotByPilotId(pilotId : string) : Promise<string | undefined> {

    const pilotDataResp = await CMSPilotApis.getPilotDataByPilotId(this.rc, pilotId)
    if (pilotDataResp.error) {
      return pilotDataResp.error
    }
    this.populateCMSPilotData(pilotDataResp.data!)
  }

  private async populateCMSPilotData(pilotData : GetCMSPilotData.retval) {

    const { utility, homeProfile, applianceProfile, recoScore } = pilotData

    this.store.dispatch(upsertCMSPilot(utility))
    this.store.dispatch(selectCMSPilot({docId : utility.documentId}))
    this.store.dispatch(setHomeProfile(homeProfile))
    this.store.dispatch(setApplianceProfile(applianceProfile))
    this.store.dispatch(setSelectedPilotRecoScore(recoScore))
    this.store.dispatch(resetRecoState())
    this.store.dispatch(resetRecoCommentsCount())
  }

}